import type { FunctionComponent } from 'react';
import { Fragment, useCallback, useEffect, useState } from 'react';

import { Button } from '../button/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../dropdown-menu/DropdownMenu';
import { IconThreeDots } from '../icons';
import { useKeyboardShortcutsEnabled } from '../keyboard-shortcuts/KeyboardShortcutsContext';
import { useHotkey } from '../keyboard-shortcuts/use-hotkey';
import type { OverflowToolbarOverflowItem } from './OverflowToolbar';

export type OverflowMenuHotkey = {
    /** Id of the toolbar item that carries this shortcut inline. */
    readonly itemId: string;
    /** Key of the `cmd`/`ctrl` combination, e.g. `K`. */
    readonly key: string;
    readonly scope: string;
};

export type OverflowMenuProps = {
    readonly align?: 'center' | 'end' | 'start';
    readonly ariaLabel: string;
    /**
     * Keeps a shortcut-carrying control reachable once it is pushed into the menu: while
     * `itemId` is in `overflow`, the combination opens the menu instead of reaching the inline
     * control. It is bound only then, so the inline control keeps sole ownership of the
     * combination whenever it is in the bar.
     */
    readonly hotkey?: OverflowMenuHotkey;
    readonly overflow: readonly OverflowToolbarOverflowItem[];
};

/**
 * Default trailing slot for `OverflowToolbar`: a "…" trigger whose dropdown renders the items
 * currently pushed to overflow. Each overflow node should be menu content (rows, submenus,
 * checkbox items) so the collapsed controls read as normal menu entries. Renders nothing while
 * there is no overflow.
 */
export const OverflowMenu: FunctionComponent<OverflowMenuProps> = ({ align = 'end', ariaLabel, hotkey, overflow }) => {
    const [open, setOpen] = useState(false);
    const keyboardShortcutsEnabled = useKeyboardShortcutsEnabled();
    const hasOverflow = overflow.length > 0;
    const hotkeyItemOverflowed = hotkey !== undefined && overflow.some((entry) => entry.id === hotkey.itemId);

    const openMenu = useCallback((event: KeyboardEvent): void => {
        event.preventDefault();
        setOpen(true);
    }, []);

    useHotkey({
        enabled: keyboardShortcutsEnabled && hotkeyItemOverflowed,
        hotkey: hotkey?.key,
        onTrigger: openMenu,
        scope: hotkey?.scope,
    });

    useEffect(() => {
        if (!hasOverflow) {
            setOpen(false);
        }
    }, [hasOverflow]);

    if (!hasOverflow) {
        return null;
    }

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger>
                <Button aria-label={ariaLabel} iconOnly={true} size="sm" variant="secondary">
                    <IconThreeDots size={16} />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={align}>
                {overflow.map((entry) => (
                    <Fragment key={entry.id}>{entry.node}</Fragment>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
